// mds-dojo — dojox/gesture/Base — FAITHFUL port of references/dojox/gesture/Base.js.
//
// An abstract parent class for gesture recognizers. It is responsible for:
//   1. Binding on() listening handlers for supported gesture events.
//   2. Monitoring underlying events and processing the 'press'|'move'|'release'|
//      'cancel' phases.
//   3. Firing and bubbling gesture events via the on() API (on.emit).
// A concrete gesture (e.g. ./tap) extends this and overrides press/move/release/
// cancel to recognize and fire its gestures.
//
// PORT NOTES (deviations from AMD source, per the mds-dojo recipe):
//   - AMD define(...) → ESM imports; classic declare() kept via the faithful
//     dojo/_base/declare-runtime (real C3 MRO + this.inherited(arguments)).
//   - dojo/_base/kernel dropped: it was only used for kernel.experimental(), which
//     the slim mds kernel does not implement.

import declare from '../../dojo/_base/declare-runtime';
import array from '../../dojo/_base/array';
import lang from '../../dojo/_base/lang';
import dom from '../../dojo/dom';
import on from '../../dojo/on';
import touch from '../../dojo/touch';
import has from '../../dojo/has';
import dojox from '../main';

lang.getObject('gesture', true, dojox);

// Declare an internal anonymous class which will only be exported by the module value.
export default declare(/*===== "dojox.gesture.Base", =====*/null, {

  // defaultEvent: [readonly] String
  //		Default event e.g. 'tap' is a default event of dojox.gesture.tap
  defaultEvent: ' ',

  // subEvents: [readonly] Array
  //		A list of sub events e.g ['hold', 'doubletap'], combined with defaultEvent
  //		like 'tap.hold', 'tap.doubletap' etc.
  subEvents: [],

  // touchOnly: boolean
  //		Whether the gesture is touch-device only
  touchOnly: false,

  // _elements: Array
  //		List of elements that wraps target node and gesture data
  _elements: null,

  constructor: function (args) {
    lang.mixin(this, args);
    this.init();
  },

  init: function () {
    // summary:
    //		Initialization works
    this._elements = [];

    if (!has('touch') && this.touchOnly) {
      console.warn('Gestures:[', this.defaultEvent, '] is only supported on touch devices!');
      return;
    }

    // bind on() handlers for various events
    var evt = this.defaultEvent;
    this.call = this._handle(evt);

    this._events = [evt];
    array.forEach(this.subEvents, function (subEvt) {
      this[subEvt] = this._handle(evt + '.' + subEvt);
      this._events.push(evt + '.' + subEvt);
    }, this);
  },

  _handle: function (/*String*/eventType) {
    // summary:
    //		Bind listen handler for the given gesture event (e.g. 'tap', 'tap.hold').
    //		The returned handle is used internally by dojo/on.
    var self = this;
    // called by dojo/on
    return function (node, listener) {
      // normalize, arguments might be (null, node, listener)
      var a = arguments;
      if (a.length > 2) {
        node = a[1];
        listener = a[2];
      }
      var isNode = node && (node.nodeType || node.attachEvent || node.addEventListener);
      if (!isNode) {
        return on(node, eventType, listener);
      } else {
        var onHandle = self._add(node, eventType, listener);
        // FIXME - users are supposed to explicitly call either
        // disconnect(signal) or signal.remove() to release resources
        var signal = {
          remove: function () {
            onHandle.remove();
            self._remove(node, eventType);
          }
        };
        return signal;
      }
    }; // dojo/on handle
  },

  _add: function (/*Dom*/node, /*String*/type, /*function*/listener) {
    // summary:
    //		Bind dojo/on handlers for both the gesture event (e.g 'tap.hold')
    //		and the underlying 'press'|'move'|'release' events.
    var element = this._getGestureElement(node);
    if (!element) {
      // the first time listening to the node
      element = {
        target: node,
        data: {},
        handles: {}
      };

      var _press = lang.hitch(this, '_process', element, 'press');
      var _move = lang.hitch(this, '_process', element, 'move');
      var _release = lang.hitch(this, '_process', element, 'release');
      var _cancel = lang.hitch(this, '_process', element, 'cancel');

      var handles = element.handles;
      if (this.touchOnly) {
        handles.press = on(node, 'touchstart', _press);
        handles.move = on(node, 'touchmove', _move);
        handles.release = on(node, 'touchend', _release);
        handles.cancel = on(node, 'touchcancel', _cancel);
      } else {
        handles.press = touch.press(node, _press);
        handles.move = touch.move(node, _move);
        handles.release = touch.release(node, _release);
        handles.cancel = touch.cancel(node, _cancel);
      }
      this._elements.push(element);
    }
    // track num of listeners for the gesture event - type
    // so that we can release element if no more gestures being monitored
    element.handles[type] = !element.handles[type] ? 1 : ++element.handles[type];

    return on(node, type, listener); // handle
  },

  _getGestureElement: function (/*Dom*/node) {
    // summary:
    //		Obtain a gesture element for the given node
    var i = 0, element;
    for (; i < this._elements.length; i++) {
      element = this._elements[i];
      if (element.target === node) {
        return element;
      }
    }
  },

  _process: function (element, phase, e) {
    // summary:
    //		Process and dispatch to appropriate phase handlers. Also provides the
    //		machinery for managing gesture bubbling.
    // description:
    //		1. e._locking is used to make sure only the most inner node will be
    //		processed for the same gesture, then the gesture bubbles up.
    //		2. Once a node starts being processed, all its descendant nodes are locked
    //		until the lock is released.
    e._locking = e._locking || {};
    if (e._locking[this.defaultEvent] || this.isLocked(e.currentTarget)) {
      return;
    }
    // invoking gesture.press()|move()|release()|cancel()
    // #16900: same condition as in dojo/touch, to avoid breaking the editing of input fields.
    if ((e.target.tagName != 'INPUT' || e.target.type == 'radio' || e.target.type == 'checkbox')
      && e.target.tagName != 'TEXTAREA') {
      e.preventDefault();
    }
    e._locking[this.defaultEvent] = true;
    this[phase](element.data, e);
  },

  press: function (data, e) {
    // summary:
    //		Process the 'press' phase of a gesture
  },
  move: function (data, e) {
    // summary:
    //		Process the 'move' phase of a gesture
  },
  release: function (data, e) {
    // summary:
    //		Process the 'release' phase of a gesture
  },
  cancel: function (data, e) {
    // summary:
    //		Process the 'cancel' phase of a gesture
  },

  fire: function (node, event) {
    // summary:
    //		Fire a gesture event and invoke registered listeners; a simulated
    //		GestureEvent is also sent along.
    if (!node || !event) {
      return;
    }
    event.bubbles = true;
    event.cancelable = true;
    (on as any).emit(node, event.type, event);
  },

  _remove: function (/*Dom*/node, /*String*/type) {
    // summary:
    //		Check and remove underlying handlers if the node is no longer being
    //		listened for 'this' gesture (all previous on() handlers removed).
    var element = this._getGestureElement(node);
    if (!element || !element.handles) { return; }

    element.handles[type]--;

    var handles = element.handles;
    if (!array.some(this._events, function (evt) {
      return handles[evt] > 0;
    })) {
      // clean up if node is not being listened anymore
      this._cleanHandles(handles);
      var i = array.indexOf(this._elements, element);
      if (i >= 0) {
        this._elements.splice(i, 1);
      }
    }
  },

  _cleanHandles: function (/*Object*/handles) {
    // summary:
    //		Clean up on handles
    for (var x in handles) {
      // remove handles for "press"|"move"|"release"|"cancel"
      if (handles[x].remove) {
        handles[x].remove();
      }
      delete handles[x];
    }
  },

  lock: function (/*Dom*/node) {
    // summary:
    //		Lock all descendants of the node.
    // tags:
    //		protected
    this._lock = node;
  },

  unLock: function () {
    // summary:
    //		Release the lock
    // tags:
    //		protected
    this._lock = null;
  },

  isLocked: function (node) {
    // summary:
    //		Check if the node is locked, i.e. whether it's a descendant of the
    //		currently locked node.
    // tags:
    //		protected
    if (!this._lock || !node) {
      return false;
    }
    return this._lock !== node && dom.isDescendant(node, this._lock);
  },

  destroy: function () {
    // summary:
    //		Release all handlers and resources
    array.forEach(this._elements, function (element) {
      this._cleanHandles(element.handles);
    }, this);
    this._elements = null;
  }
});
